import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Spinner
} from 'reactstrap'
import { Link } from 'react-router-dom'
import { getTickets, getOverdueTickets } from '../../../helpers/chat_helper'
import moment from 'moment'

const TicketList = () => {
  const [tickets, setTickets] = useState([])
  const [overdueTickets, setOverdueTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({
    status: '',
    priority: ''
  })

  useEffect(() => {
    loadTickets()
    loadOverdueTickets()
  }, [filter])

  const loadTickets = async () => {
    try {
      setLoading(true)
      const response = await getTickets(filter)
      const data = response?.data?.data?.tickets || response?.data?.tickets || []
      setTickets(data)
    } catch (error) {
      console.error('Error loading tickets:', error)
      setTickets([])
    } finally {
      setLoading(false)
    }
  }

  const loadOverdueTickets = async () => {
    try {
      const response = await getOverdueTickets()
      const data = response?.data?.data || response?.data || []
      setOverdueTickets(data)
    } catch (error) {
      console.error('Error loading overdue tickets:', error)
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'danger'
      case 'high': return 'warning'
      case 'medium': return 'info'
      case 'low': return 'secondary'
      default: return 'primary'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'danger'
      case 'in-progress': return 'warning'
      case 'resolved': return 'success'
      case 'closed': return 'secondary'
      default: return 'primary'
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <h4>Support Tickets</h4>
          </Col>
          <Col className="text-end">
            <Link to="/chat">
              <Button color="primary" size="sm">
                <i className="mdi mdi-chat"></i> Back to Chat
              </Button>
            </Link>
          </Col>
        </Row>

        {/* Overdue Tickets Alert */}
        {overdueTickets.length > 0 && (
          <Row className="mb-3">
            <Col>
              <Card className="border-danger">
                <CardBody className="bg-danger bg-soft">
                  <div className="d-flex align-items-center">
                    <i className="mdi mdi-alert-circle text-danger me-2" style={{ fontSize: '24px' }}></i>
                    <div>
                      <h6 className="mb-0 text-danger">
                        {overdueTickets.length} Overdue Ticket{overdueTickets.length > 1 ? 's' : ''}
                      </h6>
                      <small>These tickets are past their SLA deadline</small>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Row className="mb-3">
          <Col md={3}>
            <Input
              type="select"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Input>
          </Col>
          <Col md={3}>
            <Input
              type="select"
              value={filter.priority}
              onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
            >
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Input>
          </Col>
        </Row>

        {/* Tickets Table */}
        <Row>
          <Col>
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : tickets.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Ticket #</th>
                        <th>Subject</th>
                        <th>Customer</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Assigned To</th>
                        <th>Created</th>
                        <th>SLA Due</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td>
                            <Link to={`/chat/tickets/${ticket.id}`}>
                              <strong>{ticket.ticket_number}</strong>
                            </Link>
                          </td>
                          <td>{ticket.subject}</td>
                          <td>
                            {ticket.customer_name || 'N/A'}
                            <br />
                            <small className="text-muted">{ticket.customer_email}</small>
                          </td>
                          <td>
                            <Badge color={getPriorityColor(ticket.priority)}>
                              {ticket.priority}
                            </Badge>
                          </td>
                          <td>
                            <Badge color={getStatusColor(ticket.status)}>
                              {ticket.status}
                            </Badge>
                          </td>
                          <td>
                            {ticket.assignedAgent ? (
                              <>
                                {ticket.assignedAgent.first_name} {ticket.assignedAgent.last_name}
                              </>
                            ) : (
                              <span className="text-muted">Unassigned</span>
                            )}
                          </td>
                          <td>{moment(ticket.created_at).format('MMM D, YYYY')}</td>
                          <td>
                            {ticket.sla_due_at ? (
                              <span className={moment(ticket.sla_due_at).isBefore(moment()) ? 'text-danger' : ''}>
                                {moment(ticket.sla_due_at).format('MMM D, h:mm A')}
                              </span>
                            ) : 'N/A'}
                          </td>
                          <td>
                            <Link to={`/chat/tickets/${ticket.id}`}>
                              <Button color="info" size="sm">
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="mdi mdi-ticket-outline" style={{ fontSize: '64px' }}></i>
                    <h5 className="mt-3">No tickets found</h5>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default TicketList
